/**
 * Drop-in replacement for aws-amplify Auth.
 *
 * Matches the exact API shape so all 50+ call sites that destructure
 *   const { accessToken: { jwtToken } } = await Auth.currentSession();
 * continue working with zero changes.
 */

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // 30-second buffer before actual expiry
    return payload.exp * 1000 < Date.now() + 30000;
  } catch {
    return true;
  }
}

const Auth = {
  /**
   * Sign in with email + password.
   * Returns a user-like object matching Cognito's shape.
   */
  async signIn(email, password) {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || 'Login failed');
    }

    const data = await res.json();
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);

    return {
      attributes: { email },
      username: email,
      must_change_password: data.must_change_password || false,
    };
  },

  /**
   * Sign out — clear stored tokens.
   */
  async signOut() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  /**
   * Return the current session.
   * Shape matches Cognito exactly:
   *   session.accessToken.jwtToken   (property access)
   *   session.getAccessToken().getJwtToken()  (method access)
   */
  async currentSession() {
    let token = localStorage.getItem('access_token');

    if (isTokenExpired(token)) {
      token = await this._refresh();
    }

    return {
      accessToken: { jwtToken: token },
      getAccessToken() {
        return {
          getJwtToken() {
            return token;
          },
        };
      },
    };
  },

  /**
   * Return the currently authenticated user from the stored token.
   */
  async currentAuthenticatedUser() {
    const token = localStorage.getItem('access_token');
    if (!token || isTokenExpired(token)) {
      // Try refreshing before giving up
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          await this._refresh();
          const newToken = localStorage.getItem('access_token');
          const payload = JSON.parse(atob(newToken.split('.')[1]));
          return {
            attributes: { email: payload.sub },
            username: payload.sub,
          };
        } catch {
          // Fall through to throw
        }
      }
      throw new Error('No authenticated user');
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      attributes: { email: payload.sub },
      username: payload.sub,
    };
  },

  /**
   * Refresh the access token using the stored refresh token.
   * @returns {string} The new access token.
   */
  async _refresh() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) throw new Error('Session expired');

    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      throw new Error('Session expired');
    }

    const data = await res.json();
    localStorage.setItem('access_token', data.access_token);
    return data.access_token;
  },
};

export { Auth };
export default Auth;
